import { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import {
  DataTable,
  DataTableColumn,
  FilterField,
} from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import InlineFilter from '../../components/ui/inline-filter';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import ActionDropdown from '../../components/common/action-dropdown';
import { getStatusColor } from '../../utils/helpers';
import {
  Download,
  Filter,
  TrendingUp,
  DollarSign,
  BarChart3,
  FileText,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

interface Report {
  id: string;
  type: 'performance' | 'commission' | 'trade-volume' | 'revenue';
  title: string;
  period: string;
  generatedBy: string;
  generatedDate: string;
  status: 'completed' | 'pending' | 'failed';
}

const IBReports = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const reports: Report[] = [
    {
      id: '1',
      type: 'performance',
      title: 'Monthly Performance Report',
      period: 'March 2024',
      generatedBy: 'John Anderson',
      generatedDate: '2024-03-31',
      status: 'completed',
    },
    {
      id: '2',
      type: 'commission',
      title: 'Commission Summary Q1',
      period: 'Q1 2024',
      generatedBy: 'System',
      generatedDate: '2024-04-01',
      status: 'completed',
    },
    {
      id: '3',
      type: 'trade-volume',
      title: 'Trade Volume Analysis',
      period: 'March 2024',
      generatedBy: 'Michael Chen',
      generatedDate: '2024-04-02',
      status: 'completed',
    },
    {
      id: '4',
      type: 'revenue',
      title: 'Revenue Report',
      period: 'Q1 2024',
      generatedBy: 'System',
      generatedDate: '2024-04-03',
      status: 'pending',
    },
    {
      id: '5',
      type: 'performance',
      title: 'Client Performance',
      period: 'February 2024',
      generatedBy: 'Sarah Mitchell',
      generatedDate: '2024-02-29',
      status: 'completed',
    },
    {
      id: '6',
      type: 'commission',
      title: 'Weekly Commission',
      period: 'Week 13',
      generatedBy: 'System',
      generatedDate: '2024-04-05',
      status: 'failed',
    },
  ];

  const filterFields: FilterField[] = [
    {
      id: 'type',
      label: 'Report Type',
      options: [
        { value: 'performance', label: 'Performance' },
        { value: 'commission', label: 'Commission' },
        { value: 'trade-volume', label: 'Trade Volume' },
        { value: 'revenue', label: 'Revenue' },
      ],
    },
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
      ],
    },
  ];

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      if (activeFilters.type && report.type !== activeFilters.type) {
        return false;
      }
      if (activeFilters.status && report.status !== activeFilters.status) {
        return false;
      }
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        return (
          report.title.toLowerCase().includes(searchLower) ||
          report.period.toLowerCase().includes(searchLower) ||
          report.generatedBy.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [reports, activeFilters, searchValue]);

  const columns: DataTableColumn<Report>[] = [
    {
      id: 'type',
      header: 'Report Type',
      accessor: row => (
        <Badge className={getStatusColor(row.type)}>{row.type}</Badge>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '120px',
    },
    {
      id: 'title',
      header: 'Title',
      accessor: row => row.title,
      sortable: true,
      sortValue: row => row.title,
      width: '120px',
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => row.period,
      sortable: true,
      sortValue: row => row.period,
      width: '140px',
    },
    {
      id: 'generatedBy',
      header: 'Generated By',
      accessor: row => row.generatedBy,
      sortable: true,
      sortValue: row => row.generatedBy,
      width: '150px',
    },
    {
      id: 'generatedDate',
      header: 'Generated Date',
      accessor: row => row.generatedDate,
      sortable: true,
      sortValue: row => row.generatedDate,
      width: '160px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>{row.status}</Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={false}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View report:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="IB Reports"
          description="View and generate IB performance and analytics reports"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Reports"
          value={filteredReports.length.toString()}
          change="+2 from last month"
          icon={FileText}
        />
        <StatsCard
          title="Performance Reports"
          value={filteredReports
            .filter(r => r.type === 'performance')
            .length.toString()}
          change="33% of total"
          icon={TrendingUp}
        />
        <StatsCard
          title="Commission Reports"
          value={filteredReports
            .filter(r => r.type === 'commission')
            .length.toString()}
          change="33% of total"
          icon={DollarSign}
        />
        <StatsCard
          title="Trade Volume Reports"
          value={filteredReports
            .filter(r => r.type === 'trade-volume')
            .length.toString()}
          change="17% of total"
          icon={BarChart3}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Performance Reports</CardTitle>
              <CardDescription>
                View and manage all generated reports
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <InlineFilter
            showFilters={showFilters}
            onToggle={() => setShowFilters(!showFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={() => setActiveFilters({})}
            onFiltersApply={() => {}}
            title="Filter Reports"
          />
          <DataTable
            columns={columns}
            data={filteredReports}
            searchValue={searchValue}
            searchPlaceholder="Search reports..."
            onSearchChange={setSearchValue}
            onSearchReset={() => setSearchValue('')}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFiltersChange={() => {}}
            showColumnVisibility={true}
            showAddButton={false}
            showExportButton={false}
            initialPageSize={10}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IBReports;
